<template>
  <v-card flat>
    <v-card-title class="pa-0">
      <h4>Onboard {{ allSteps.title }} Dependencies</h4>
    </v-card-title>
    <v-card-text class="px-0">
      <p class="px-0">
        <span v-if="stepDependencies.length === 0">
          This asset has no {{ allSteps.title }} dependencies to review
        </span>
        <span v-else>
          Please review the {{ allSteps.title }} dependencies values below and
          make any desired changes
        </span>
      </p>
      <div v-if="stepDependencies.length !== 0">
        <div class="dependency-table">
          <sphere-table
            ref="step-dependencies-table"
            :data-items="stepDependencies"
            :columns="tableColumns"
            name="Asset Review - Account Dependencies"
            table-type="actionable"
            :configurable-table-key="`arm-account-dependencies-${allSteps.title
              .toLowerCase()
              .replace(/ /g, '-')}`"
            @selectionchange="updateSelection"
          >
            <template #below-header="{ props }">
              <span class="header-bulk-actions">
                <span
                  v-if="
                    [
                      'ShouldBeOnboarded',
                      'CanRestartService',
                      'DisableAutomaticManagement',
                    ].includes(props.field)
                  "
                >
                  <span
                    v-if="props.field === 'ShouldBeOnboarded'"
                    class="font-italic red--text"
                  >
                    Required:
                  </span>
                  <span v-else class="font-italic black--text">
                    Optional:
                  </span>
                  <v-btn
                    v-for="option in ['Yes', 'No']"
                    :key="option"
                    :disabled="
                      selectedItems.length === 0 ||
                      (props.field !== 'ShouldBeOnboarded' &&
                        !shouldBeOnboardedFlag)
                    "
                    small
                    class="sb_btn_solid"
                    color="v-btn--outline primary--text"
                    :title="option"
                    :value="option === 'Yes'"
                    @click.stop="setAll(props.field, option)"
                  >
                    {{ option }}
                  </v-btn>
                </span>
                <div
                  v-else-if="props.field === 'DisableAutomaticManagementReason'"
                  v-show="
                    props.field === 'DisableAutomaticManagementReason'
                      ? selectedItems.find(
                          (i) => i.DisableAutomaticManagement
                        ) !== null
                      : true
                  "
                >
                  <span class="d-inline-flex">
                    <span class="font-italic black--text pt-2">
                      Optional:
                    </span>
                    <v-text-field
                      v-model="disableAutomaticManagementReasonValue"
                      class="mx-2 p-0 mt-1"
                      outline
                      @change="(newValue) => setAll(props.field, newValue)"
                    />
                  </span>
                </div>
                <div v-else-if="props.field === 'TaskFolder'">
                  <span class="d-inline-flex">
                    <span class="font-italic black--text pt-2">
                      Optional:
                    </span>
                    <v-text-field
                      v-model="taskFolderValue"
                      class="mx-2 p-0 mt-1"
                      outline
                      @change="(newValue) => setAll(props.field, newValue)"
                    />
                  </span>
                </div>
              </span>
            </template>
            <template #shouldBeOnboardedTemplate="{ props }">
              <td>
                <v-btn-toggle
                  :key="props.dataItem.InstanceId"
                  v-model="props.dataItem.ShouldBeOnboarded"
                  mandatory
                  class="my-1"
                  @click.stop
                >
                  <v-btn
                    v-for="option in ['Yes', 'No']"
                    :key="option"
                    small
                    class="sb_btn_solid"
                    :title="option"
                    :value="option"
                    @click.stop="
                      setSingleValue(
                        props.dataItem,
                        'ShouldBeOnboarded',
                        option
                      )
                    "
                  >
                    {{ option }}
                  </v-btn>
                </v-btn-toggle>
              </td>
            </template>
            <template #canRestartServiceTemplate="{ props }">
              <td>
                <v-btn-toggle
                  :key="props.dataItem.InstanceId"
                  v-model="props.dataItem.CanRestartService"
                  :disabled="props.dataItem.ShouldBeOnboarded === 'No'"
                  class="my-1"
                  @click.stop
                >
                  <v-btn
                    v-for="option in ['Yes', 'No']"
                    :key="option"
                    small
                    class="sb_btn_solid"
                    :title="option"
                    :disabled="props.dataItem.ShouldBeOnboarded === 'No'"
                    :value="option"
                    @click.stop="
                      setSingleValue(
                        props.dataItem,
                        'CanRestartService',
                        option
                      )
                    "
                  >
                    {{ option }}
                  </v-btn>
                </v-btn-toggle>
              </td>
            </template>
            <template #disableAutomaticManagementTemplate="{ props }">
              <td>
                <v-btn-toggle
                  :key="props.dataItem.InstanceId"
                  v-model="props.dataItem.DisableAutomaticManagement"
                  :disabled="props.dataItem.ShouldBeOnboarded === 'No'"
                  class="my-1"
                  @click.stop
                >
                  <v-btn
                    v-for="option in ['Yes', 'No']"
                    :key="option"
                    small
                    class="sb_btn_solid"
                    :title="option"
                    :disabled="props.dataItem.ShouldBeOnboarded === 'No'"
                    :value="option"
                    @click.stop="
                      setSingleValue(
                        props.dataItem,
                        'DisableAutomaticManagement',
                        option
                      )
                    "
                  >
                    {{ option }}
                  </v-btn>
                </v-btn-toggle>
              </td>
            </template>
            <template #disableAutomaticManagementReasonTemplate="{ props }">
              <td>
                <v-text-field
                  v-show="
                    props.dataItem.ShouldBeOnboarded === 'Yes' &&
                    props.dataItem.DisableAutomaticManagement === 'Yes'
                  "
                  :value="props.dataItem.DisableAutomaticManagementReason"
                  class="my-1"
                  outline
                  @click.stop
                  @input="
                    (newValue) => {
                      setSingleValue(
                        props.dataItem,
                        'DisableAutomaticManagementReason',
                        newValue
                      );
                    }
                  "
                />
              </td>
            </template>
            <template #taskFolderTemplate="{ props }">
              <td>
                <v-text-field
                  v-show="props.dataItem.ShouldBeOnboarded === 'Yes'"
                  :value="props.dataItem.TaskFolder"
                  class="my-1"
                  outline
                  @click.stop
                  @input="
                    (newValue) => {
                      setSingleValue(props.dataItem, 'TaskFolder', newValue);
                    }
                  "
                />
              </td>
            </template>
          </sphere-table>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
/**
 * In this review step the owner is shown a list of account dependencies - which
 * are places the account is used to run a service, scheduled task, or app pool
 */
import reviewStepBase from './reviewStepBase.mixin.js';
import SphereTable from '../.././Table/SphereTable.vue';

export default {
  name: 'AssetReviewStepDependencies',
  components: { SphereTable },
  mixins: [reviewStepBase],
  data() {
    return {
      stepDependencies: [],
      selectedItems: [],
      disableAutomaticManagementReasonValue: '',
      taskFolderValue: '',
      shouldBeOnboardedFlag: true,
    };
  },
  computed: {
    tableColumns() {
      return [
        { field: 'ServiceTypeId', hidden: true },
        { field: 'ServiceTypeName', hidden: true },
        { field: 'InstanceId', hidden: true },
        { field: 'Folder', hidden: true },
        { field: 'AccountSTSGuid', hidden: true },
        { field: 'HostName', title: 'Host Name', orderIndex: 0 },
        { field: 'InstanceName', title: 'Instance Name', orderIndex: 1 },
        { field: 'AccountName', orderIndex: 2 },
        {
          field: 'ShouldBeOnboarded',
          title: 'Should Be Onboarded',
          cell: 'shouldBeOnboardedTemplate',
          filter: 'text',
          orderIndex: 3,
        },
        {
          field: 'CanRestartService',
          title: 'Can Restart Service',
          cell: 'canRestartServiceTemplate',
          filter: 'text',
          orderIndex: 4,
          hidden: this.allSteps.title === 'Scheduled Task',
        },
        {
          field: 'TaskFolder',
          title: 'Task Folder',
          cell: 'taskFolderTemplate',
          orderIndex: 5,
          hidden: this.allSteps.title !== 'Scheduled Task',
        },
        {
          field: 'DisableAutomaticManagement',
          title: 'Disable Automatic Management',
          cell: 'disableAutomaticManagementTemplate',
          filter: 'text',
          orderIndex: 6,
        },
        {
          field: 'DisableAutomaticManagementReason',
          title: 'Disable Automatic Management Reason',
          cell: 'disableAutomaticManagementReasonTemplate',
          orderIndex: 7,
        },
      ];
    },
  },
  watch: {
    dependencies: {
      handler(newValue) {
        this.stepDependencies = newValue
          .filter(
            (d) =>
              d.ServiceTypeName.toLowerCase() ==
              this.allSteps.title.toLowerCase()
          )
          .map((dep) => {
            return {
              ShouldBeOnboarded: 'Yes',
              CanRestartService:
                this.allSteps.title === 'Scheduled Task' ? undefined : 'No',
              DisableAutomaticManagement: 'No',
              DisableAutomaticManagementReason: null,
              TaskFolder: dep.Folder,
              ...dep,
            };
          });
        this.$emit('onboard-dependencies', this.stepDependencies);
      },
      deep: true,
    },
  },
  methods: {
    /**
     * Set a property value, accounting for the dependency between some fields
     */
    // eslint-disable-next-line complexity
    setPropertyValue(obj, field, value) {
      // Can't prodive a reason if it's not being disabled
      if (
        field === 'DisableAutomaticManagementReason' &&
        obj.DisableAutomaticManagement !== 'Yes'
      ) {
        return;
      }
      this.$set(obj, field, value);
      // if we're enabling automatic management, clear out the reason
      if (
        field === 'DisableAutomaticManagement' &&
        (value === 'No' || value === null)
      ) {
        this.$set(obj, 'DisableAutomaticManagementReason', null);
      }
    },
    /**
     * Event handler for clicking a button within a row
     */
    setSingleValue(obj, field, value) {
      this.setDependenciesToOnboardColumn(obj, field, value);
      this.clearAllOverTextField();
      this.$emit('onboard-dependencies', [obj]);
    },
    /**
     * Event handler for clicking a button in the header
     */
    setAll(field, value) {
      this.selectedItems.forEach((obj) => {
        this.setDependenciesToOnboardColumn(obj, field, value);
      });
      this.clearAllOverTextField();
      this.$emit('onboard-dependencies', this.selectedItems);
      this.$nextTick(() => {
        this.$refs['step-dependencies-table'].items.forEach((p, idx) => {
          this.selectedItems.forEach((i) => {
            if (i.InstanceId === p.InstanceId)
              this.$refs['step-dependencies-table'].items[idx] = i;
          });
        });
        this.$refs.table.setFilteredItems({
          items: this.$refs['step-dependencies-table'].items,
        });
      });
    },
    /**
     * Event handler for when the user selects rows, maintaining a copy here for
     * bulk actions
     */
    updateSelection(selectedItems) {
      this.selectedItems = selectedItems;
      this.shouldBeOnboardedFlag = this.selectedItems.some(
        (x) => x.ShouldBeOnboarded != 'No'
      );
    },
    clearAllOverTextField() {
      if (
        this.selectedItems.filter(
          (i) => i.DisableAutomaticManagementReason != null
        ).length == 0
      ) {
        this.disableAutomaticManagementReasonValue = null;
      }
      if (this.selectedItems.filter((i) => i.TaskFolder != null).length == 0) {
        this.taskFolderValue = null;
      }
    },
    setDependenciesToOnboardColumn(obj, field, value) {
      if (field === 'ShouldBeOnboarded') {
        this.setPropertyValue(obj, field, value);
        if (value == 'No') {
          obj.CanRestartService =
            this.allSteps.title === 'Scheduled Task' ? undefined : 'No';
          obj.DisableAutomaticManagement = 'No';
          obj.DisableAutomaticManagementReason = null;
          obj.TaskFolder = null;
        }
      } else if (obj.ShouldBeOnboarded === 'Yes') {
        this.setPropertyValue(obj, field, value);
      }
      this.shouldBeOnboardedFlag = this.selectedItems.some(
        (x) => x.ShouldBeOnboarded != 'No'
      );
    },
  },
};
</script>
<style lang="scss">
.dependency-table {
  .sphere-table-container {
    position: relative !important;

    /* Make active button blue (copied from AssetReviewStepAccess.vue) */
    .v-item-group > .v-btn--active {
      background-color: rgb(25, 118, 210);
      color: white;
    }
    /* Make the text inputs smaller to they don't vertically stretch the rows */
    .v-text-field {
      padding-top: 0;
      input {
        padding: 0;
        height: auto;
      }
      .v-text-field__details {
        display: none;
      }
      .v-input__slot {
        min-height: 25px;
      }
    }
    td {
      .v-text-field {
        .v-input__control {
          box-sizing: border-box;
          input {
            margin: 0;
          }
        }
      }
    }
    /*
    Ensure all the under-header inputs take up the same amount of space to
    avoid mis-aligning the header text
    */
    .header-bulk-actions {
      height: 33px;
      display: block;
      background: #f6f6f6;
      padding: 0 4px;
      box-sizing: border-box;
      input {
        margin: 0;
      }
      button {
        margin-top: 4px;
        margin-right: 0;
        height: 24px;
      }
    }
  }
}
</style>
